import { useAuthorization } from 'dx-sdk/build/authorization';
import {
  CTAPlannerCard,
  FeatureCard,
  FeaturesGrid,
  Hero,
  IconCard,
  LinkArrow,
  Picture,
  PlannerCard,
  PlannerField,
  PlannerFields,
  RemoteSVG,
  Stack,
  Typography,
} from 'dx-sdk/build/components';
import { Pencil, PencilAndPaper } from 'dx-sdk/build/icons';
import { Theme as ThemeProvider } from 'dx-sdk/build/providers';
import { formatDealerName } from 'pubweb-smokey/dist/utils/formatters';
import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, Redirect, useLocation } from 'react-router-dom';
import App from '../../../App';
import { DXAccountContext } from '../../../contexts/DXAccountContext';
import PlannerHeroBG from '../../../images/PlannerHeroBG.jpg';
import PlannerCTAIcon from '../../../images/portal/cta-planner-card-icon.PNG';
import plannerCTAAccent from '../../../images/portal/plannerCTAAccent.svg';
import plannerCardLinkIcon from '../../../images/portal/plannerCardLinkIcon.svg';
import Loading from '../../../images/svg/icons/Loading';
import * as dealerService from '../../../services/dealerService';
import * as homesService from '../../../services/homesService';
import { fireSegmentEvent, parseInitialPropsContext } from '../../../utils';
import PlannerForm from '../../components/PlannerForm/PlannerForm';
import BudgetIcon from '../../components/PlannerIcons/BudgetIcon';
import HomeAspirationsIcon from '../../components/PlannerIcons/HomeAspirationsIcon';
import HomeBuyingJourneyIcon from '../../components/PlannerIcons/HomeBuyingJourneyIcon';
import HomePlannerIcon from '../../components/PlannerIcons/HomePlannerIcon';
import LandIcon from '../../components/PlannerIcons/LandIcon';
import {
  buyerSituationAnswer,
  confidenceAnswer,
  fundingAnswer,
  getPlannerContent,
  getTotalAverageProgress,
  isPlannerComplete,
  landAnswer,
  portalIconProgress,
  setMoveDateFormat,
} from '../../utils/PortalUtils';
import MyHomePlannerStyles from './MyHomePlanner.styled';

const MyHomePlanner = ({ dealer, homes }) => {
  const dxContext = useContext(DXAccountContext);
  const [updatedUserProfile, setUpdatedUserProfile] = useState(null);
  const { isAuthenticated, isLoading } = useAuthorization();
  const CTAContent = getPlannerContent(
    dxContext?.state?.account?.plannerProgress
  );
  const budgetObj = dxContext?.state?.account?.plannerProgress?.find(
    (item) => item.section == 'Budget'
  );
  const journeyObj = dxContext?.state?.account?.plannerProgress?.find(
    (item) => item.section == 'Home Buying Journey'
  );
  const landObj = dxContext?.state?.account?.plannerProgress?.find(
    (item) => item.section == 'Land'
  );
  const aspirationsObj = dxContext?.state?.account?.plannerProgress?.find(
    (item) => item.section == 'Home Aspirations'
  );
  const budgetProgress = portalIconProgress(budgetObj?.percentCompleted);
  const homeBuyingJourneyProgress = portalIconProgress(
    journeyObj?.percentCompleted
  );
  const homeAspirationsProgress = portalIconProgress(
    aspirationsObj?.percentCompleted
  );
  const landProgress = portalIconProgress(landObj?.percentCompleted);
  const totalAvg = getTotalAverageProgress(
    dxContext?.state?.account?.plannerProgress
  );
  const questionsAreComplete = isPlannerComplete(
    dxContext?.state?.account?.plannerProgress
  );
  const mainProgressIcon = portalIconProgress(totalAvg);

  const location = useLocation();

  const ctaPlannerLinkPath = () => {
    if (journeyObj?.percentCompleted !== 100) {
      return '/portal/home-buying-journey';
    } else if (budgetObj?.percentCompleted !== 100) {
      return '/portal/budget';
    } else if (landObj?.percentCompleted !== 100) {
      return '/portal/land';
    } else if (aspirationsObj?.percentCompleted !== 100) {
      return '/portal/my-home-aspirations';
    } else {
      return '/portal/home-buying-journey';
    }
  };

  useEffect(() => {
    if (dxContext?.state?.account?.profile) {
      setUpdatedUserProfile(dxContext?.state?.account?.profile);
    }
  }, [dxContext?.state?.account?.profile]);

  const CustomAction = (value, path, questionNumber, segmentVariable) => {
    return (
      <Link
        className="DXButton DXButton-root"
        to={{
          pathname: path,
          state: { dealer, homes, activeQuestionLink: questionNumber },
        }}
        onClick={() => {
          fireSegmentEvent('CTA Clicked', {
            isNav: false,
            type: 'button',
            text: value ? 'Edit' : 'COMPLETE YOUR ANSWER',
            eventContext: segmentVariable,
          });
        }}
      >
        {value ? (
          <>
            <Pencil /> <span className="hide-on-mobile">Edit</span>
          </>
        ) : (
          <>
            <PencilAndPaper />
            <span className="hide-on-mobile">COMPLETE YOUR ANSWER</span>
          </>
        )}
      </Link>
    );
  };

  if (!isAuthenticated && !isLoading) {
    return <Redirect to="/myhome-account-login" />;
  }

  return (
    <ThemeProvider theme={{}}>
      <App dealer={dealer} homes={homes}>
        <MyHomePlannerStyles>
          <Helmet>
            <title>
              Your Home Planner | {formatDealerName(dealer?.dealerName)}
            </title>
            <meta name="robots" content="noindex" />
          </Helmet>

          {dxContext?.state?.globalLoading ? (
            <div className="loading-screen-panel">
              <Loading width="150px" />
            </div>
          ) : (
            <>
              <Hero
                components={{
                  Backdrop: Picture,
                }}
                componentsProps={{
                  Backdrop: {
                    src: PlannerHeroBG,
                  },
                  Container: {
                    style: {
                      margin: '0px auto',
                    },
                  },
                }}
              >
                <IconCard
                  heading="Your Home Planner"
                  icon={
                    <HomePlannerIcon
                      dashArray={mainProgressIcon?.dashArray}
                      dashOffset={mainProgressIcon?.dashOffset}
                      opacity={mainProgressIcon?.opacity}
                      showStar={mainProgressIcon?.showStar}
                    />
                  }
                  iconOverlay
                >
                  <Typography>
                    The Home Planner is designed to walk you through each step
                    of the home buying journey. When you’re ready, submit your
                    information to a home consultant who can help finalize your
                    purchase.
                  </Typography>
                  {questionsAreComplete ? (
                    <Link
                      className="cta-button"
                      to={{
                        pathname: '/portal/homes-for-you',
                        state: { dealer, homes },
                      }}
                      onClick={() => {
                        fireSegmentEvent('CTA Clicked', {
                          isNav: false,
                          type: 'button',
                          text: 'Shop Your Preferences',
                          eventContext: 'portal_planner_homes_for_you_card',
                        });
                      }}
                    >
                      Shop Your Preferences
                    </Link>
                  ) : (
                    <Link
                      className="cta-button"
                      to={{
                        pathname: ctaPlannerLinkPath(),
                        state: { dealer, homes },
                      }}
                    >
                      {totalAvg === 0
                        ? 'Start your planner'
                        : 'Continue your planner'}
                    </Link>
                  )}
                </IconCard>
              </Hero>
              <PlannerForm
                dealer={dealer}
                homes={homes}
                showPlannerForm={questionsAreComplete}
              />
              <div className="container">
                <div className="planner-overview-wrap">
                  <PlannerCard
                    icon={
                      <HomeBuyingJourneyIcon
                        dashArray={homeBuyingJourneyProgress?.dashArray}
                        dashOffset={homeBuyingJourneyProgress?.dashOffset}
                        opacity={homeBuyingJourneyProgress?.opacity}
                        showStar={homeBuyingJourneyProgress?.showStar}
                      />
                    }
                    link={'/'}
                    heading="Understand the Home Buying Journey"
                    subheading="The home buying journey can be overwhelming. We can help you navigate it by breaking down the process and providing helpful resources."
                    components={{
                      Link: () => (
                        <Link
                          to={{
                            pathname: '/portal/home-buying-journey/',
                            state: { dealer, homes },
                          }}
                          onClick={() => {
                            fireSegmentEvent('CTA Clicked', {
                              isNav: false,
                              type: 'card',
                              text: 'Understand the Home Buying Journey',
                              eventContext: 'portal_planner_home_buying_card',
                            });
                          }}
                        >
                          <LinkArrow />
                        </Link>
                      ),
                    }}
                  >
                    {(updatedUserProfile?.buyerSituation ||
                      updatedUserProfile?.targetMoveDateMonth ||
                      updatedUserProfile?.targetMoveDateYear) && (
                      <PlannerFields
                        heading="Your Home Journey"
                        subheading="Your Answers"
                      >
                        <PlannerField
                          heading="Reason for looking"
                          answer={buyerSituationAnswer(
                            updatedUserProfile?.buyerSituation
                          )}
                          components={{
                            Action: () =>
                              CustomAction(
                                updatedUserProfile?.buyerSituation,
                                '/portal/home-buying-journey/',
                                1,
                                'portal_planner_reason_for_looking_edit'
                              ),
                          }}
                        />
                        <PlannerField
                          heading="Target purchase date"
                          answer={setMoveDateFormat(
                            updatedUserProfile?.targetMoveDateMonth,
                            updatedUserProfile?.targetMoveDateYear
                          )}
                          components={{
                            Action: () =>
                              CustomAction(
                                updatedUserProfile?.targetMoveDateMonth &&
                                  updatedUserProfile?.targetMoveDateYear,
                                '/portal/home-buying-journey/',
                                2,
                                'portal_planner_target_purchase_date_edit'
                              ),
                          }}
                        />
                      </PlannerFields>
                    )}
                  </PlannerCard>
                  <PlannerCard
                    icon={
                      <BudgetIcon
                        dashArray={budgetProgress?.dashArray}
                        dashOffset={budgetProgress?.dashOffset}
                        opacity={budgetProgress?.opacity}
                        showStar={budgetProgress?.showStar}
                      />
                    }
                    link={'/'}
                    heading="Determine Your Budget"
                    subheading="Choosing a home that fits your needs also means finding a home that fits your budget. Get BudgetSmart® by using our helpful budgeting tools."
                    components={{
                      Link: () => (
                        <Link
                          to={{
                            pathname: '/portal/budget/',
                            state: { dealer, homes },
                          }}
                          onClick={() => {
                            fireSegmentEvent('CTA Clicked', {
                              isNav: false,
                              type: 'card',
                              text: 'Determine Your Budget',
                              eventContext: 'portal_planner_budget_card',
                            });
                          }}
                        >
                          <LinkArrow />
                        </Link>
                      ),
                    }}
                  >
                    {(updatedUserProfile?.fundingSource ||
                      dxContext?.state?.account?.profile?.budget ||
                      updatedUserProfile?.budgetConfidence) && (
                      <PlannerFields
                        heading="Your Budget"
                        subheading="Your Answers"
                      >
                        <PlannerField
                          heading="How you will be purchasing your home"
                          answer={fundingAnswer(
                            updatedUserProfile?.fundingSource
                          )}
                          components={{
                            Action: () =>
                              CustomAction(
                                updatedUserProfile?.fundingSource,
                                '/portal/budget/',
                                1,
                                'portal_planner_purchasing_home_edit'
                              ),
                          }}
                        />
                        <PlannerField
                          heading="Your budget is"
                          answer={
                            dxContext?.state?.account?.profile?.budget
                              ? `$${dxContext?.state?.account?.profile?.budget.toLocaleString(
                                  'en-US'
                                )}`
                              : 'No budget set'
                          }
                          components={{
                            Action: () =>
                              CustomAction(
                                dxContext?.state?.account?.profile?.budget,
                                '/portal/budget/',
                                2,
                                'portal_planner_budget_edit'
                              ),
                          }}
                        />
                        <PlannerField
                          heading="Your budget confidence level is"
                          answer={confidenceAnswer(
                            updatedUserProfile?.budgetConfidence
                          )}
                          components={{
                            Action: () =>
                              CustomAction(
                                updatedUserProfile?.budgetConfidence,
                                '/portal/budget/',
                                3,
                                'portal_planner_budget_confidence_edit'
                              ),
                          }}
                        />
                      </PlannerFields>
                    )}
                  </PlannerCard>
                  <PlannerCard
                    icon={
                      <LandIcon
                        dashArray={landProgress?.dashArray}
                        dashOffset={landProgress?.dashOffset}
                        opacity={landProgress?.opacity}
                        showStar={landProgress?.showStar}
                      />
                    }
                    link={'/'}
                    heading="Learn About Land"
                    subheading="Whether or not you own land can greatly impact your overall timeline. Tell us more about the location of your future home and learn more about purchasing land, site preparation and other land-related topics."
                    components={{
                      Link: () => (
                        <Link
                          to={{
                            pathname: '/portal/land/',
                            state: { dealer, homes },
                          }}
                          onClick={() => {
                            fireSegmentEvent('CTA Clicked', {
                              isNav: false,
                              type: 'card',
                              text: 'Learn About Land',
                              eventContext: 'portal_planner_land_card',
                            });
                          }}
                        >
                          <LinkArrow />
                        </Link>
                      ),
                    }}
                  >
                    {(updatedUserProfile?.landType ||
                      updatedUserProfile?.landZip) && (
                      <PlannerFields
                        heading="Your Land Needs"
                        subheading="Your Answers"
                      >
                        <PlannerField
                          heading="Land for your home"
                          answer={landAnswer(updatedUserProfile?.landType)}
                          components={{
                            Action: () =>
                              CustomAction(
                                updatedUserProfile?.hasLand !== null
                                  ? true
                                  : updatedUserProfile?.landType,
                                '/portal/land/',
                                1,
                                'portal_planner_has_land_edit'
                              ),
                          }}
                        />
                        <PlannerField
                          heading="Land location"
                          answer={
                            updatedUserProfile?.landZip ||
                            `You haven't set a land location`
                          }
                          components={{
                            Action: () =>
                              CustomAction(
                                updatedUserProfile?.landZip,
                                '/portal/land/',
                                2,
                                'portal_planner_land_location_edit'
                              ),
                          }}
                        />
                      </PlannerFields>
                    )}
                  </PlannerCard>
                  <PlannerCard
                    icon={
                      <HomeAspirationsIcon
                        dashArray={homeAspirationsProgress?.dashArray}
                        dashOffset={homeAspirationsProgress?.dashOffset}
                        opacity={homeAspirationsProgress?.opacity}
                        showStar={homeAspirationsProgress?.showStar}
                      />
                    }
                    link={'/'}
                    heading="Dream About Your Home"
                    subheading="Share the features, size and layout that you envision in your next home to help us recommend homes that fit your lifestyle. Find more information about available options on the Home Aspirations page."
                    components={{
                      Link: () => (
                        <Link
                          to={{
                            pathname: '/portal/my-home-aspirations/',
                            state: { dealer, homes },
                          }}
                          onClick={() => {
                            fireSegmentEvent('CTA Clicked', {
                              isNav: false,
                              type: 'card',
                              text: 'Dream About Your Home',
                              eventContext:
                                'portal_planner_my_home_aspirations_card',
                            });
                          }}
                        >
                          <LinkArrow />
                        </Link>
                      ),
                    }}
                  >
                    {(updatedUserProfile?.preferredBeds ||
                      updatedUserProfile?.preferredBaths ||
                      dxContext?.state?.account?.profile?.likedFeatures.length >
                        0) && (
                      <PlannerFields
                        heading="Home Preferences"
                        subheading="Your Answers"
                      >
                        <Stack
                          direction={{
                            large: 'row',
                          }}
                          justifyContent="space-evenly"
                          fullwidth
                        >
                          <PlannerField
                            heading="Beds"
                            answer={
                              updatedUserProfile?.preferredBeds
                                ? updatedUserProfile?.preferredBeds == '1'
                                  ? updatedUserProfile?.preferredBeds +
                                    ' bedroom'
                                  : updatedUserProfile?.preferredBeds +
                                    ' bedrooms'
                                : 'No preference'
                            }
                            components={{
                              Action: () =>
                                CustomAction(
                                  updatedUserProfile?.preferredBeds,
                                  '/portal/my-home-aspirations/',
                                  1,
                                  'portal_planner_beds_edit'
                                ),
                            }}
                          />
                          <PlannerField
                            heading="Baths"
                            answer={
                              updatedUserProfile?.preferredBaths
                                ? updatedUserProfile?.preferredBaths == '1'
                                  ? updatedUserProfile?.preferredBaths +
                                    ' bathroom'
                                  : updatedUserProfile?.preferredBaths +
                                    ' bathrooms'
                                : 'No preference'
                            }
                            components={{
                              Action: () =>
                                CustomAction(
                                  updatedUserProfile?.preferredBaths,
                                  '/portal/my-home-aspirations/',
                                  1,
                                  'portal_planner_baths_edit'
                                ),
                            }}
                          />
                        </Stack>
                        {dxContext?.state?.account?.profile?.likedFeatures ? (
                          <div className="feature-answer-wrap">
                            <h2>Favorite Features</h2>
                            <FeaturesGrid>
                              {dxContext?.state?.account?.profile?.likedFeatures.map(
                                (feature) => (
                                  <FeatureCard
                                    icon={<RemoteSVG src={feature?.url} />}
                                    label={feature.name}
                                    featureId={feature.featureId}
                                    key={feature.featureId}
                                    favorite={true}
                                  />
                                )
                              )}
                              <Link
                                className="edit-features-btn"
                                to={{
                                  pathname: '/portal/my-home-aspirations',
                                  state: {
                                    dealer,
                                    homes,
                                    activeQuestionLink: 2,
                                  },
                                }}
                                onClick={() => {
                                  fireSegmentEvent('CTA Clicked', {
                                    isNav: false,
                                    type: 'button',
                                    text: '',
                                    eventContext:
                                      'portal_planner_favorite_features_edit',
                                  });
                                }}
                              >
                                <Pencil />
                                Edit Favorite Features
                              </Link>
                            </FeaturesGrid>
                          </div>
                        ) : (
                          <PlannerField
                            heading="Favorite Features"
                            answer={
                              'You haven’t selected your favorite features'
                            }
                            components={{
                              Action: () =>
                                CustomAction(
                                  updatedUserProfile?.preferredBaths,
                                  '/portal/my-home-aspirations/',
                                  1,
                                  'portal_planner_favorite_features_edit'
                                ),
                            }}
                          />
                        )}
                      </PlannerFields>
                    )}
                  </PlannerCard>
                  {CTAContent?.PlannerCTAContent && (
                    <CTAPlannerCard
                      accentImg={{ src: plannerCTAAccent, alt: 'CTA Icon' }}
                      clickComponent={{ type: '', link: '' }}
                      chip="Explore"
                      heading={CTAContent.PlannerCTAContent.headline}
                      icon={PlannerCTAIcon}
                      components={{
                        Click: () => (
                          <Link
                            to={{
                              pathname: CTAContent.PlannerCTAContent.link,
                              state: { dealer, homes },
                            }}
                            onClick={() => {
                              fireSegmentEvent('CTA Clicked', {
                                isNav: false,
                                type: 'card',
                                text:
                                  CTAContent?.PlannerCTAContent?.headline || '',
                                eventContext: 'portal_planner_card_cta',
                              });
                            }}
                          >
                            <img
                              src={plannerCardLinkIcon}
                              alt="planner card link icon"
                            />
                          </Link>
                        ),
                      }}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </MyHomePlannerStyles>
      </App>
    </ThemeProvider>
  );
};

MyHomePlanner.getInitialProps = async (ctx) => {
  return await parseInitialPropsContext(ctx, dealerService, homesService).catch(
    (e) => {
      console.error(e);
      return { redirectTo: '/error' };
    }
  );
};

export default MyHomePlanner;
